// V9.24 · DRE Comparativo: resultado mês a mês, variação e alertas de margem
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, Download, TrendingUp } from 'lucide-react';
import api from '@/lib/api';

const money = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
const MES = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const Var = ({ value, suffix = '', invert = false }) => {
  if (value === undefined || value === null) return <span className="text-muted-foreground text-xs">—</span>;
  const bom = invert ? value <= 0 : value >= 0;
  const Icon = value >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-semibold tabular-nums ${
      value === 0 ? 'text-muted-foreground' : bom ? 'text-emerald-700' : 'text-rose-700'}`}>
      <Icon className="w-3 h-3" />
      {value > 0 ? '+' : ''}{value}{suffix}
    </span>
  );
};

export const DreAnualTab = ({ filtros = {} }) => {
  const anos = useMemo(() => {
    const y = new Date().getFullYear();
    return [y + 1, y, y - 1, y - 2];
  }, []);
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get('/fin/ledger/dre-year', { params: { year, ...filtros } });
      setData(d);
    } catch (_) { toast.error('Erro ao carregar o DRE comparativo.'); }
    finally { setLoading(false); }
  }, [year, JSON.stringify(filtros)]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const maxAbs = useMemo(() => Math.max(
    1, ...(data?.meses || []).map((m) => Math.abs(m.resultado))), [data]);

  const exportarCsv = () => {
    const head = ['Competência', 'Receita bruta', 'Deduções', 'Receita líquida', 'Despesas',
      'Resultado', 'Margem %', 'Var. resultado', 'Var. margem p.p.'];
    const rows = (data?.meses || []).map((m) => [m.competencia, m.receita_bruta, m.deducoes,
      m.receita_liquida, m.despesas, m.resultado, m.margem,
      m.var_resultado ?? '', m.var_margem ?? '']);
    const csv = [head, ...rows].map((r) => r.join(';')).join('\n');
    const url = URL.createObjectURL(new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url; a.download = `dre-comparativo-${year}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const t = data?.totais;

  return (
    <div className="space-y-3" data-testid="tab-dre-anual">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="dre-ano"><SelectValue /></SelectTrigger>
            <SelectContent>{anos.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
          </Select>
          <p className="text-sm text-muted-foreground">
            {t?.meses_com_movimento || 0} mês(es) com movimento · média {money(t?.media_resultado)}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={exportarCsv} data-testid="dre-exportar">
          <Download className="w-4 h-4 mr-1" /> Exportar CSV
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <div className="rounded-xl border p-4 border-emerald-300 bg-emerald-50/60" data-testid="dre-ano-receita">
          <p className="text-xs font-bold uppercase text-muted-foreground">Receita líquida do ano</p>
          <p className="text-2xl font-bold tabular-nums">{money(t?.receita_liquida)}</p>
          <p className="text-xs text-muted-foreground">Bruta {money(t?.receita_bruta)} · deduções {money(t?.deducoes)}</p>
        </div>
        <div className="rounded-xl border p-4 border-rose-300 bg-rose-50/60" data-testid="dre-ano-despesas">
          <p className="text-xs font-bold uppercase text-muted-foreground">Despesas do ano</p>
          <p className="text-2xl font-bold tabular-nums">{money(t?.despesas)}</p>
        </div>
        <div className="rounded-xl border p-4 border-foreground/30 bg-foreground/5" data-testid="dre-ano-resultado">
          <p className="text-xs font-bold uppercase text-muted-foreground">Resultado do ano</p>
          <p className="text-2xl font-bold tabular-nums">{money(t?.resultado)}</p>
          <p className="text-xs text-muted-foreground">Margem {t?.margem ?? 0}%</p>
        </div>
        <div className="rounded-xl border p-4" data-testid="dre-ano-extremos">
          <p className="text-xs font-bold uppercase text-muted-foreground">Melhor × pior mês</p>
          <p className="text-sm mt-1 flex items-center gap-1">
            <TrendingUp className="w-4 h-4 text-emerald-600" />
            {data?.melhor_mes ? `${data.melhor_mes.competencia} · ${money(data.melhor_mes.resultado)}` : '—'}
          </p>
          <p className="text-sm flex items-center gap-1">
            <ArrowDownRight className="w-4 h-4 text-rose-600" />
            {data?.pior_mes ? `${data.pior_mes.competencia} · ${money(data.pior_mes.resultado)}` : '—'}
          </p>
        </div>
      </div>

      {!!data?.alertas?.length && (
        <Card className="border-amber-300 bg-amber-50/60" data-testid="dre-alertas">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-700" /> Alertas do ano ({data.alertas.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            {data.alertas.map((a, i) => (
              <p key={i} className="text-sm flex items-start gap-2" data-testid={`dre-alerta-${i}`}>
                <Badge variant="outline" className={a.nivel === 'danger'
                  ? 'bg-rose-100 text-rose-800 border-rose-300' : 'bg-amber-100 text-amber-800 border-amber-300'}>
                  {a.nivel === 'danger' ? 'Crítico' : 'Atenção'}
                </Badge>
                <span>{a.mensagem}</span>
              </p>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Mês</TableHead>
              <TableHead className="text-right">Receita bruta</TableHead>
              <TableHead className="text-right">(−) Deduções</TableHead>
              <TableHead className="text-right">Receita líquida</TableHead>
              <TableHead className="text-right">(−) Despesas</TableHead>
              <TableHead className="text-right">Resultado</TableHead>
              <TableHead className="text-right">Margem</TableHead>
              <TableHead>Variação</TableHead>
              <TableHead className="w-32">Evolução</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={9} className="text-center py-8">Carregando…</TableCell></TableRow>}
              {!loading && (data?.meses || []).map((m) => (
                <TableRow key={m.competencia} data-testid={`dre-row-${m.competencia.replace('/', '-')}`}
                  className={!m.tem_movimento ? 'opacity-45' : ''}>
                  <TableCell className="font-medium capitalize">{MES[m.mes - 1]}/{String(year).slice(2)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(m.receita_bruta)}</TableCell>
                  <TableCell className="text-right tabular-nums text-amber-700">{money(m.deducoes)}</TableCell>
                  <TableCell className="text-right tabular-nums">{money(m.receita_liquida)}</TableCell>
                  <TableCell className="text-right tabular-nums text-rose-700">{money(m.despesas)}</TableCell>
                  <TableCell className={`text-right font-bold tabular-nums ${m.resultado < 0 ? 'text-rose-700' : ''}`}>
                    {money(m.resultado)}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{m.tem_movimento ? `${m.margem}%` : '—'}</TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <Var value={m.var_resultado} />
                      {m.var_margem !== undefined && <Var value={m.var_margem} suffix=" p.p." />}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="h-2 w-28 rounded-full bg-muted overflow-hidden flex">
                      <div className={`h-full rounded-full transition-all ${m.resultado < 0 ? 'bg-rose-500' : 'bg-emerald-500'}`}
                        style={{ width: `${Math.min(100, (Math.abs(m.resultado) / maxAbs) * 100)}%` }} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DreAnualTab;
